import type { User } from "@supabase/auth-js"
import type { SupabaseClient } from "@supabase/supabase-js"
import { createContext, useContext } from "react"
import {
	PRIVATE_CACHE_CONTROL,
	SHARED_PAGE_CACHE_CONTROL,
	hasAuthCookie,
} from "./auth-cookie.ts"

import { resolveRequestSession } from "./auth-session.ts"

// server

type AuthRequest = { request: Request; fresh?: boolean }

export const getAuthFromRequest = async ({ request, fresh }: AuthRequest) => {
	const cookieHeader = request.headers.get("Cookie")
	const { user, setCookies } = await resolveRequestSession(request, fresh)
	const headers = new Headers()
	headers.set(
		"Cache-Control",
		hasAuthCookie(cookieHeader)
			? PRIVATE_CACHE_CONTROL
			: SHARED_PAGE_CACHE_CONTROL,
	)
	for (const cookie of setCookies) headers.append("Set-Cookie", cookie)
	return { user, headers }
}

export const getUserFromRequest = async ({ request, fresh }: AuthRequest) => {
	const { user } = await getAuthFromRequest({ request, fresh })
	return user
}

export const getUserIdFromRequest = async ({ request, fresh }: AuthRequest) => {
	const user = await getUserFromRequest({ request, fresh })
	return user?.id
}

// client: the provider owns a single auth subscription for the whole app.

interface AuthContext {
	/** The client once its code has loaded. To use it, call `getSupabase`. */
	supabase?: SupabaseClient
	/** The client, loading its code first when no one has needed it yet. Rejects when the code can't be loaded. */
	getSupabase: () => Promise<SupabaseClient>
	user: User | null
	loading: boolean
	/** Better Auth is on (BETTER_AUTH_ENABLED): there is no Supabase client, and the member comes from the root loader. */
	betterAuth: boolean
	/** The cut-over notice is on (AUTH_MAINTENANCE): the pages show it instead of their forms. */
	maintenance: boolean
	/** Asks the server who is signed in now, after a sign-in or sign-out through Better Auth. */
	refreshUser: () => void
}

export const AuthContext = createContext<AuthContext>({
	supabase: undefined,
	getSupabase: () => Promise.reject(new Error("No auth provider")),
	user: null,
	loading: true,
	betterAuth: false,
	maintenance: false,
	refreshUser: () => {},
})

export function useSupabase() {
	return useContext(AuthContext)
}

export const useUser = () => {
	const { user, loading } = useContext(AuthContext)
	return { user, loading }
}
